import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeSVG } from "qrcode.react";
import { Stack, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box } from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import { profileStore } from "@/stores/Stores";

const TwoFactorAuthentication: React.FC<{
  open: boolean;
  onCancel?: () => void;
  onConfirm: (code: string) => void;
}> = ({ open, onCancel = () => {}, onConfirm }) => {
  const { t } = useTranslation();
  const inputRefs = Array.from({ length: 6 }, () => useRef<HTMLInputElement>(null));
  const [code, setCode] = useState<string[]>(Array(6).fill(""));

  useEffect(() => {
    if (!open) {
      setCode(Array(6).fill(""));
    }
  }, [open]);

  const issuer = "Relux";
  const secret = profileStore.secret2FA as string;
  const user = profileStore.profile?.email || "user";
  const otpAuthUrl = `otpauth://totp/${issuer}:${user}?secret=${secret}&issuer=${issuer}`;

  const handleChange = (idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 1); // Only allow single digit
    const newCode = [...code];

    newCode[idx] = value;
    setCode(newCode);

    if (value && idx < inputRefs.length - 1) {
      inputRefs[idx + 1].current?.focus();
    }
  };

  const validateCode = () => {
    if (code.some(v => v === "")) {
      return false;
    }

    profileStore.verificationCode2FA = code;
    return true;
  };

  const handleKeyDown = (idx: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !code[idx] && idx > 0) {
      inputRefs[idx - 1].current?.focus();
    }
  };

  const handleConfirm = () => {
    const joinedCode = code.join("");
    if (joinedCode.length !== 6) {
      return;
    }
    onConfirm(joinedCode);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(secret);
  };

  return (
    <Dialog maxWidth="md" open={open}>
      <DialogTitle>{t("Two-Factor Authentication")}</DialogTitle>
      <DialogContent>
        <Stack alignItems="center" justifyContent="center" mb="1rem" p="1rem">
          <QRCodeSVG value={otpAuthUrl} size={128} />
        </Stack>
        <Stack component="ol" gap="1rem" mb="1rem" pl="1rem">
          <Box component="li">{t("To get started, install an authenticator app such as Authy, Ente Auth, or Google Authenticator on your phone")}.</Box>
          <Box component="li">
            {t("Scan the QR code with your authenticator app on your phone, or enter the code manually")}:
            <Stack direction="row" alignItems="center" justifyContent="center" mt="1rem">
              <Box
                component="code"
                sx={{
                  display: "inline-flex",
                  fontWeight: "bold",
                  wordBreak: "break-word",
                  letterSpacing: "1px",
                }}
              >
                {secret?.match(/.{1,4}/g)?.join(" ")}
              </Box>
              <IconButton title={t("Copy to clipboard")} onClick={copyToClipboard} sx={{ cursor: "pointer" }}>
                <ContentCopyRoundedIcon fontSize="small" color="primary" />
              </IconButton>
            </Stack>
          </Box>
          <Box component="li">{t("Enter the code generated by your authenticator app below")}:</Box>
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} m="1rem 0" p="1rem">
          {inputRefs.map((ref, idx) => (
            <TextField
              key={idx}
              inputRef={ref}
              autoFocus={idx === 0}
              variant="outlined"
              size="medium"
              type="number"
              slotProps={{
                htmlInput: { maxLength: 1, style: { textAlign: "center" } },
              }}
              sx={{ width: "2.5rem" }}
              value={code[idx]}
              onChange={handleChange(idx)}
              onKeyDown={handleKeyDown(idx)}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>{t("Cancel")}</Button>
        <Button disabled={!validateCode()} onClick={handleConfirm} variant="contained">
          {t("Activate")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TwoFactorAuthentication;
